// Login / session — 05-admin §2.2.
//
// PRODUCTION: there is NO in-app login form. Cloudflare Access (Zero Trust)
// fronts admin.{domain} and /v1/admin/*; the operator authenticates against the
// company IdP at the perimeter and the Access JWT is forwarded automatically.
// This view exists only to (a) show the current operator identity via
// /v1/auth/me and (b) provide a DEV bearer-token field for local development
// where the Access perimeter is not present.
import { api, errMsg } from "../core/api.js";
import { h, mount } from "../core/dom.js";
import { navigate } from "../core/router.js";
import { clearDevToken, loadDevToken, saveDevToken } from "../core/tokens.js";
import type { Me } from "../models.js";

export async function loginView(_p: Record<string, string>, root: HTMLElement): Promise<void> {
  const tokenField = h("input", {
    class: "field",
    type: "password",
    placeholder: "dev bearer token (로컬 개발 전용)",
    value: loadDevToken() ?? "",
  }) as HTMLInputElement;
  const status = h("p", { class: "small muted" }, "");
  const identity = h("div", { class: "state muted" }, "신원 확인 중…");

  async function checkIdentity(): Promise<void> {
    identity.replaceChildren(document.createTextNode("신원 확인 중…"));
    try {
      // /v1/auth/me (01 §3.A) — current operator identity & role.
      const me = await api.get<Me>("/v1/auth/me");
      const okRole = me.role === "moderator" || me.role === "admin";
      identity.className = "state";
      identity.replaceChildren(
        h(
          "dl",
          { class: "kv" },
          h("dt", {}, "handle"),
          h("dd", {}, "@" + me.handle),
          h("dt", {}, "role"),
          h(
            "dd",
            {},
            h("span", { class: `badge ${okRole ? "ok" : "danger"}` }, me.role),
            okRole ? null : " — 콘솔 접근 불가 (moderator+ 필요)",
          ),
          h("dt", {}, "capabilities"),
          h("dd", {}, (me.capabilities ?? []).join(", ") || "—"),
        ),
      );
    } catch (e) {
      identity.className = "state error";
      identity.replaceChildren(
        document.createTextNode(`신원 확인 실패: ${errMsg(e)}`),
      );
    }
  }

  function saveToken(): void {
    const v = tokenField.value.trim();
    if (v) {
      saveDevToken(v);
      status.textContent = "dev 토큰 저장됨.";
      status.className = "small ok";
    } else {
      clearDevToken();
      status.textContent = "dev 토큰 삭제됨.";
      status.className = "small muted";
    }
    void checkIdentity();
  }

  mount(
    root,
    h(
      "div",
      { class: "stack narrow" },
      h("h1", {}, "로그인 / 세션"),
      h(
        "section",
        { class: "card" },
        h("h2", {}, "Cloudflare Access (프로덕션)"),
        h(
          "p",
          { class: "muted small" },
          "프로덕션에서는 Cloudflare Access(Zero Trust)가 전단에서 인증합니다. 콘솔 자체 로그인 폼은 없으며, Access 세션이 자동으로 전달됩니다. 세션 만료 시 Access 재인증이 필요합니다 (05-admin §2.2).",
        ),
        identity,
      ),
      h(
        "section",
        { class: "card" },
        h("h2", {}, "로컬 개발 토큰"),
        h(
          "p",
          { class: "muted small" },
          "로컬 개발(Access 전단 없음)에서는 moderator/admin 액세스 JWT를 붙여 Authorization 헤더로 사용합니다.",
        ),
        tokenField,
        h(
          "div",
          { class: "row" },
          h("button", { class: "btn", onclick: saveToken }, "저장 / 확인"),
          h("button", { class: "btn secondary", onclick: () => navigate("/") }, "대시보드로"),
        ),
        status,
      ),
    ),
  );

  await checkIdentity();
}
